import React, {ReactNode, useMemo} from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';

import {AppButtonVariant} from './models/interfaces';

import './styles';

type AppButtonProps = {
  label?: string;
  isLoading?: boolean;
  variant?: AppButtonVariant;
  disabled?: null | boolean | undefined;
  disabledOnPress?: null | boolean | undefined;
  color?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  labelColor?: string;
  isDark?: boolean;
  isAuto?: boolean;
  mobileStickyStyle?: boolean;
  removeStyles?: boolean;
  customWidthButton?: string;
  id?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  onClick: () => void;
};

const getButtonClass = (variant: AppButtonVariant): string => {
  switch (variant) {
    case AppButtonVariant.onPrimary:
      return 'on-primary';
    case AppButtonVariant.onSecondary:
      return 'on-secondary';
    case AppButtonVariant.onTertiary:
      return 'on-tertiary';
    case AppButtonVariant.onQuaternary:
      return 'on-quaternary';
    case AppButtonVariant.onQuinary:
      return 'on-quinary';
    case AppButtonVariant.onSenary:
      return 'on-senary';
    case AppButtonVariant.onText:
      return 'on-text';
    case AppButtonVariant.devTool:
      return 'on-dev';
    case AppButtonVariant.onHelp:
      return 'on-help';
    case AppButtonVariant.onCheckout:
      return 'on-checkout';
    case AppButtonVariant.onPrimaryGreen:
      return 'on-primary-green';
    case AppButtonVariant.onTertiaryGreen:
      return 'on-tertiary-green';
    default:
      return '';
  }
};

const getLoadingClass = (variant: AppButtonVariant): string => {
  switch (variant) {
    case AppButtonVariant.onPrimary:
      return 'loading-on-primary';
    default:
      return '';
  }
};

export const AppButton: React.FC<AppButtonProps> = ({
  isLoading = false,
  onClick,
  label,
  disabled,
  mobileStickyStyle = false,
  variant = AppButtonVariant.onPrimary,
  isAuto = false,
  iconLeft,
  iconRight,
  removeStyles = false,
  customWidthButton,
  color,
  labelColor,
  id,
  borderColor,
  borderWidth,
  borderRadius,
}) => {
  const buttonClass = getButtonClass(variant);
  const loadingClass = useMemo(() => {
    if (!isLoading) return '';
    return getLoadingClass(variant);
  }, [isLoading, variant]);
  const containerClass = `button-container ${mobileStickyStyle ? 'sticky-button-container' : ''}`;
  const isFullWidth = !isAuto;

  const isDisabled = useMemo(() => {
    return disabled || isLoading;
  }, [disabled, isLoading]);

  const showLoader = useMemo(() => {
    if (!isLoading) return null;
    return (
      <CircularProgress
        size={18}
        color="inherit"
        className="icon-right loader"
      />
    );
  }, [isLoading]);

  const buttonStyle = useMemo(() => {
    return {
      ...(color && {backgroundColor: color}),
      ...(borderColor && {
        border: `${borderWidth || '1px'} solid ${borderColor}`,
      }),
      ...(borderRadius && {borderRadius}),
    };
  }, [color, borderColor, borderWidth, borderRadius]);

  const labelStyle = useMemo(() => {
    return labelColor ? {color: labelColor} : {};
  }, [labelColor]);

  return (
    <section
      className={containerClass}
      style={removeStyles ? null : {width: isFullWidth ? '100%' : 'auto'}}>
      <Button
        id={id}
        sx={customWidthButton ? {width: customWidthButton} : {}}
        className={`${buttonClass} ${loadingClass}`.trim()}
        onClick={onClick}
        disabled={isDisabled}
        style={buttonStyle}>
        {iconLeft && <span className="icon-left">{iconLeft}</span>}
        <label
          className="text"
          style={labelStyle}>
          {label}
        </label>
        {iconRight && <span className="icon-right">{iconRight}</span>}
        {showLoader}
      </Button>
    </section>
  );
};
